(function (root) {
  'use strict';

  const MAX_QUANTITY = 99;
  const fail = (code, message) => Object.assign(new Error(message || code), { code });
  const cents = (value) => Number.isSafeInteger(value) && value >= 0;
  const identifier = (value) => {
    const id = String(value == null ? '' : value);
    if (!/^\d+$/.test(id) || !Number.isSafeInteger(Number(id)) || Number(id) <= 0) throw fail('invalidCatalog');
    return id;
  };
  const quantity = (value) => {
    if ((typeof value !== 'number' && typeof value !== 'string') || (typeof value === 'string' && !/^\d+$/.test(value))) throw fail('invalidQuantity');
    const number = Number(value);
    if (!Number.isSafeInteger(number) || number < 1 || number > MAX_QUANTITY) throw fail('invalidQuantity');
    return number;
  };
  const moneySum = (a, b) => {
    const result = a + b;
    if (!cents(result)) throw fail('invalidPrice');
    return result;
  };
  const tierFor = (units) => units >= 4 ? 10 : units === 3 ? 8 : units === 2 ? 5 : 0;

  function normalizeSize(value) {
    const exact = String(value == null ? '' : value);
    const aliases = { xs: 'XS', 'extra small': 'XS', s: 'S', small: 'S', m: 'M', medium: 'M', l: 'L', large: 'L', xl: 'XL', 'extra large': 'XL' };
    const key = exact.trim().toLowerCase();
    return Object.prototype.hasOwnProperty.call(aliases, key) ? aliases[key] : exact;
  }

  function tierProgress(model) {
    if (!model || model.suppressProjection || model.error || !Number.isSafeInteger(model.eligibleUnits) || model.eligibleUnits < 0) return null;
    const units = model.eligibleUnits;
    const target = units < 2 ? 2 : units < 3 ? 3 : units < 4 ? 4 : 4;
    return { units, tier: tierFor(units), remaining: Math.max(0, target - units), nextTier: tierFor(target), maximum: units >= 4, value: Math.min(4, units), max: 4 };
  }

  function projectedTotal(model) {
    if (!model || model.suppressProjection || model.error || !cents(model.actualCartSubtotal) || !cents(model.selectedSubtotal) || !cents(model.savings)) return null;
    const applied = model.appliedBundleSavings == null ? 0 : model.appliedBundleSavings;
    if (!cents(applied)) return null;
    // The cart subtotal already includes its native bundle discount. Restore
    // that amount before applying the projected tier to the combined bundle.
    const total = moneySum(moneySum(model.actualCartSubtotal, applied), model.selectedSubtotal) - model.savings;
    return cents(total) ? total : null;
  }


  function classification(product, variant) {
    if (!product || !variant || !cents(variant.price)) return 'unverified';
    if (product.saleSignal === true || variant.saleSignal === true) return 'sale';
    if (variant.compareAt !== null && !cents(variant.compareAt)) return 'unverified';
    if (variant.compareAt > variant.price || variant.eligibility === 'sale') return 'sale';
    return variant.eligibility === 'regular' ? 'regular' : 'unverified';
  }

  function indexCatalog(products) {
    if (!Array.isArray(products)) throw fail('invalidCatalog');
    const variants = new Map();
    const productIds = new Set();
    const normalized = products.map((product) => {
      if (!product || !Array.isArray(product.variants) || !product.variants.length) throw fail('invalidCatalog');
      const id = identifier(product.id);
      if (productIds.has(id)) throw fail('invalidCatalog');
      productIds.add(id);
      const item = { ...product, id, title: String(product.title || ''), type: String(product.type || ''), tags: Array.isArray(product.tags) ? product.tags : [] };
      item.variants = product.variants.map((variant) => {
        if (!variant || !cents(variant.price)) throw fail('invalidCatalog');
        const variantId = identifier(variant.id);
        if (variants.has(variantId)) throw fail('invalidCatalog');
        const max = variant.maxQuantity == null ? (variant.max == null ? null : variant.max) : variant.maxQuantity;
        if (max !== null && (!Number.isSafeInteger(max) || max < 0)) throw fail('invalidCatalog');
        const value = { ...variant, id: variantId, productId: id, title: String(variant.title || ''), size: String(variant.size || variant.title || ''), available: variant.available === true, max, eligibility: classification(product, variant), product: item };
        variants.set(variantId, value);
        return value;
      });
      return item;
    });
    return { products: normalized, variants };
  }

  function aggregateSelections(selections) {
    if (!Array.isArray(selections)) throw fail('invalidQuantity');
    const aggregate = new Map();
    selections.forEach((item) => {
      const id = identifier(item.id == null ? item.variantId : item.id);
      const count = quantity(item.quantity);
      aggregate.set(id, quantity((aggregate.get(id) || 0) + count));
    });
    return Array.from(aggregate, ([id, count]) => ({ id, quantity: count }));
  }

  function validateCart(cart) {
    if (!cart || !Array.isArray(cart.items) || !cents(cart.total_price)) throw fail('invalidCart');
    cart.items.forEach((item) => {
      identifier(item.variant_id == null ? item.id : item.variant_id);
      if (!Number.isSafeInteger(item.quantity) || item.quantity < 1) throw fail('invalidCart');
    });
    return cart;
  }

  function cartQuantities(cart) {
    validateCart(cart);
    const result = new Map();
    cart.items.forEach((item) => {
      const id = identifier(item.variant_id == null ? item.id : item.variant_id);
      const next = (result.get(id) || 0) + item.quantity;
      if (!Number.isSafeInteger(next)) throw fail('invalidCart');
      result.set(id, next);
    });
    return result;
  }

  function lineDiscounted(item) {
    return ['discounts', 'discount_allocations', 'line_level_discount_allocations'].some((key) => Array.isArray(item[key]) && item[key].length > 0)
      || (cents(item.total_discount) && item.total_discount > 0)
      || (cents(item.original_price) && cents(item.final_price) && item.final_price < item.original_price)
      || (cents(item.original_line_price) && cents(item.final_line_price) && item.final_line_price < item.original_line_price)
      || (cents(item.price) && cents(item.final_price) && item.final_price < item.price)
      || (cents(item.line_price) && cents(item.final_line_price) && item.final_line_price < item.line_price);
  }

  function cartLineEligible(index, item, currentQuantities) {
    const variant = index.variants.get(identifier(item.variant_id == null ? item.id : item.variant_id));
    const original = cents(item.original_price) ? item.original_price : item.price;
    const simple = !item.selling_plan_allocation && !item.parent_relationship && !item.remote && !(item.item_components && item.item_components.length);
    return !!(variant && variant.available && variant.eligibility === 'regular' && simple
      && (variant.max === null || currentQuantities.get(variant.id) <= variant.max)
      && cents(original) && original === variant.price && cents(original * item.quantity));
  }

  function discountContext(cart, index) {
    const discountedIds = new Set();
    const malformedList = (object, key) => object[key] != null && !Array.isArray(object[key]);
    cart.items.forEach((item) => {
      if (lineDiscounted(item) || ['discounts', 'discount_allocations', 'line_level_discount_allocations'].some((key) => malformedList(item, key))
        || (item.total_discount != null && !cents(item.total_discount))) discountedIds.add(identifier(item.variant_id == null ? item.id : item.variant_id));
    });
    const cartWide = malformedList(cart, 'cart_level_discount_applications') || (Array.isArray(cart.cart_level_discount_applications) && cart.cart_level_discount_applications.length > 0)
      || malformedList(cart, 'discount_applications') || (Array.isArray(cart.discount_applications) && cart.discount_applications.length > 0);
    const unattributed = (cart.total_discount != null && (!cents(cart.total_discount) || cart.total_discount > 0))
      || (cents(cart.items_subtotal_price) && cart.total_price < cart.items_subtotal_price)
      || (cents(cart.original_total_price) && cart.original_total_price > cart.total_price);
    const hasDiscounts = cartWide || unattributed || discountedIds.size > 0;
    const context = { discountedIds, suppressProjection: hasDiscounts, hasDiscounts, bundleDiscountLines: new Set(), appliedBundleSavings: 0, nativeBundleTier: 0 };
    if (!hasDiscounts || cartWide || !index || !cents(cart.total_discount) || cart.total_discount === 0
      || !cents(cart.items_subtotal_price) || !cents(cart.original_total_price)) return context;

    const titles = { 'FULLSTOP Bundle 2 • 5% off regular items': 5, 'FULLSTOP Bundle 3 • 8% off regular items': 8, 'FULLSTOP Bundle 4+ • 10% off regular items': 10 };
    const quantities = cartQuantities(cart);
    const eligible = cart.items.filter((item) => cartLineEligible(index, item, quantities));
    const currentTier = tierFor(eligible.reduce((sum, item) => sum + item.quantity, 0));
    if (!currentTier) return context;
    const eligibleLines = new Set(eligible);
    let allocated = 0;
    let originalTotal = 0;
    let finalTotal = 0;
    for (const item of cart.items) {
      if (!['original_price', 'final_price', 'original_line_price', 'final_line_price', 'total_discount'].every((key) => cents(item[key]))
        || item.original_line_price !== item.original_price * item.quantity
        || item.final_line_price !== item.original_line_price - item.total_discount
        || item.final_price < Math.floor(item.final_line_price / item.quantity) || item.final_price > Math.ceil(item.final_line_price / item.quantity)
        || (item.price != null && (!cents(item.price) || (item.price !== item.original_price && item.price !== item.final_price)))
        || (item.line_price != null && (!cents(item.line_price) || (item.line_price !== item.original_line_price && item.line_price !== item.final_line_price)))
        || ['discounts', 'discount_allocations', 'line_level_discount_allocations'].some((key) => malformedList(item, key))
        || (item.discount_allocations && item.discount_allocations.length)) return context;
      originalTotal = moneySum(originalTotal, item.original_line_price);
      finalTotal = moneySum(finalTotal, item.final_line_price);
      if (!eligibleLines.has(item)) {
        if (lineDiscounted(item)) return context;
        continue;
      }
      const expected = Number((BigInt(item.original_line_price) * BigInt(currentTier) + 50n) / 100n);
      const allocations = item.line_level_discount_allocations;
      if (!expected || item.total_discount !== expected || !Array.isArray(allocations) || allocations.length !== 1) return context;
      const allocation = allocations[0];
      const application = allocation && allocation.discount_application;
      if (!application || !cents(allocation.amount) || allocation.amount !== expected
        || typeof application.title !== 'string' || !Object.prototype.hasOwnProperty.call(titles, application.title) || titles[application.title] !== currentTier
        || !['', 'automatic'].includes(application.type) || application.value_type !== 'percentage'
        || !['number', 'string'].includes(typeof application.value)
        || !new RegExp('^' + currentTier + '(?:\\.0+)?$').test(String(application.value))
        || application.target_type !== 'line_item' || application.target_selection !== 'entitled' || application.allocation_method !== 'each'
        || application.total_allocated_amount !== cart.total_discount) return context;
      if (item.discounts && item.discounts.length && (item.discounts.length !== 1 || !item.discounts[0]
        || item.discounts[0].title !== application.title || item.discounts[0].amount !== expected)) return context;
      allocated = moneySum(allocated, expected);
    }
    if (allocated !== cart.total_discount || originalTotal !== cart.original_total_price || finalTotal !== cart.items_subtotal_price
      || finalTotal !== cart.total_price || originalTotal - finalTotal !== allocated) return context;
    context.suppressProjection = false;
    context.bundleDiscountLines = eligibleLines;
    context.appliedBundleSavings = allocated;
    context.nativeBundleTier = currentTier;
    return context;
  }

  function validateSelection(index, cart, selections) {
    const result = aggregateSelections(selections);
    const inCart = cartQuantities(cart);
    result.forEach((item) => {
      const variant = index.variants.get(item.id);
      if (!variant) throw fail('unknownVariant');
      if (!variant.available) throw fail('unavailable');
      if (variant.max !== null && (inCart.get(item.id) || 0) + item.quantity > variant.max) throw fail('stock');
      const rule = variant.quantityRule || {};
      const combined = (inCart.get(item.id) || 0) + item.quantity;
      const min = rule.min == null ? 1 : rule.min;
      const increment = rule.increment == null ? 1 : rule.increment;
      if (!Number.isSafeInteger(min) || min < 1 || !Number.isSafeInteger(increment) || increment < 1 || (rule.max != null && (!Number.isSafeInteger(rule.max) || rule.max < 1))) throw fail('quantityRule');
      if (combined < min || combined % increment !== 0 || (rule.max != null && combined > rule.max)) throw fail('quantityRule');
    });
    return result;
  }

  // Check only the candidate's combined quantity. Other stale staged lines must
  // remain visible and must not prevent the shopper from repairing them one by one.
  function canStageVariant(index, cart, selections, variant, count = 1, replaceId = '') {
    if (!variant || !variant.available) return false;
    try {
      const staged = aggregateSelections(selections.filter((item) => String(item.id) !== String(replaceId)));
      const current = staged.find((item) => item.id === variant.id);
      validateSelection(index, cart, [{ id: variant.id, quantity: quantity((current ? current.quantity : 0) + quantity(count)) }]);
      return true;
    } catch (_) { return false; }
  }

  function matchingSizeVariants(index, cart, selections, product, size, count = 1, replaceId = '') {
    if (!product || product.hasSize === false || !normalizeSize(size)) return [];
    return product.variants.filter((variant) => variant.eligibility === 'regular' && normalizeSize(variant.size) === normalizeSize(size) && canStageVariant(index, cart, selections, variant, count, replaceId));
  }

  function prioritizeProducts(products, index, cart, selections, size) {
    if (!normalizeSize(size)) return products.slice();
    return products.map((product, order) => ({ product, order, matching: matchingSizeVariants(index, cart, selections, product, size).length > 0 }))
      .sort((a, b) => Number(b.matching) - Number(a.matching) || a.order - b.order).map((item) => item.product);
  }

  function sameSizeAlternatives(index, cart, selections, source, size, count = 1, replaceId = '') {
    if (!source || source.hasSize === false || !source.type.trim() || !normalizeSize(size)) return [];
    return index.products.filter((product) => product.id !== source.id && product.type === source.type)
      .flatMap((product) => matchingSizeVariants(index, cart, selections, product, size, count, replaceId)).slice(0, 3);
  }

  function selectionIssue(index, cart, item) {
    try { validateSelection(index, cart, [item]); return null; }
    catch (error) { return error; }
  }

  function preview(index, cart, selections) {
    validateCart(cart);
    const staged = validateSelection(index, cart, selections);
    const context = discountContext(cart, index);
    const currentQuantities = cartQuantities(cart);
    let selectedSubtotal = 0;
    let selectedUnits = 0;
    let stagedEligibleUnits = 0;
    let cartEligibleUnits = 0;
    let eligibleSubtotal = 0;
    let excludedUnits = 0;
    const lines = staged.map((item) => {
      const variant = index.variants.get(item.id);
      const subtotal = variant.price * item.quantity;
      if (!cents(subtotal)) throw fail('invalidPrice');
      const eligible = variant.eligibility === 'regular' && !context.suppressProjection;
      selectedSubtotal = moneySum(selectedSubtotal, subtotal);
      selectedUnits += item.quantity;
      if (eligible) { stagedEligibleUnits += item.quantity; eligibleSubtotal = moneySum(eligibleSubtotal, subtotal); }
      else excludedUnits += item.quantity;
      return { id: item.id, quantity: item.quantity, subtotal, eligible, variant };
    });
    cart.items.forEach((item) => {
      const lineTotal = cents(item.final_line_price) ? item.final_line_price : item.line_price;
      const original = cents(item.original_price) ? item.original_price : item.price;
      const recognizedBundle = context.bundleDiscountLines.has(item);
      const matchesPrice = cents(lineTotal) && lineTotal === original * item.quantity;
      const eligible = !context.suppressProjection && cartLineEligible(index, item, currentQuantities)
        && (recognizedBundle || (matchesPrice && !lineDiscounted(item)));
      if (eligible) { cartEligibleUnits += item.quantity; eligibleSubtotal = moneySum(eligibleSubtotal, original * item.quantity); }
      else excludedUnits += item.quantity;
    });
    const eligibleUnits = stagedEligibleUnits + cartEligibleUnits;
    const tier = tierFor(eligibleUnits);
    const savings = Number((BigInt(eligibleSubtotal) * BigInt(tier) + 50n) / 100n);
    if (!cents(savings)) throw fail('invalidPrice');
    return { lines, selectedUnits, selectedSubtotal, actualCartTotal: cart.total_price, actualCartSubtotal: cents(cart.items_subtotal_price) ? cart.items_subtotal_price : cart.total_price, stagedEligibleUnits, cartEligibleUnits, eligibleUnits, eligibleSubtotal, tier, savings, excludedUnits, hasDiscounts: context.hasDiscounts, suppressProjection: context.suppressProjection, appliedBundleSavings: context.appliedBundleSavings, nativeBundleTier: context.nativeBundleTier };
  }

  function addPayload(index, cart, selections) {
    const items = validateSelection(index, cart, selections);
    if (!items.length) throw fail('empty');
    return { items: items.map((item) => ({ id: Number(item.id), quantity: item.quantity })) };
  }

  function sameQuantities(left, right) {
    const a = cartQuantities(left);
    const b = cartQuantities(right);
    return a.size === b.size && Array.from(a).every(([id, count]) => b.get(id) === count);
  }

  function verifyAddition(before, after, selections) {
    const expected = cartQuantities(before);
    aggregateSelections(selections).forEach((item) => expected.set(item.id, (expected.get(item.id) || 0) + item.quantity));
    const actual = cartQuantities(after);
    return expected.size === actual.size && Array.from(expected).every(([id, count]) => actual.get(id) === count);
  }

  function snapshot(index, cart, selections) {
    const ids = new Set([...cartQuantities(cart).keys(), ...aggregateSelections(selections).map((item) => item.id)]);
    // Shopify can regenerate application keys/timestamps on every cart read.
    // Preserve all pricing/eligibility evidence but omit that volatile metadata.
    const application = (value) => value && typeof value === 'object' ? Object.fromEntries(Object.keys(value).sort().filter((key) => !['key', 'created_at', 'updated_at'].includes(key)).map((key) => [key, value[key]])) : value;
    const allocations = (values) => Array.isArray(values) ? values.map((value) => value && typeof value === 'object' ? { ...value, ...(value.discount_application ? { discount_application: application(value.discount_application) } : {}) } : value) : values;
    const items = cart.items.map((item) => ({ ...item, ...(item.line_level_discount_allocations != null ? { line_level_discount_allocations: allocations(item.line_level_discount_allocations) } : {}), ...(item.discount_allocations != null ? { discount_allocations: allocations(item.discount_allocations) } : {}) }));
    const cartPricing = { items, total_price: cart.total_price, items_subtotal_price: cart.items_subtotal_price, original_total_price: cart.original_total_price, total_discount: cart.total_discount, cart_level_discount_applications: Array.isArray(cart.cart_level_discount_applications) ? cart.cart_level_discount_applications.map(application) : cart.cart_level_discount_applications, discount_applications: Array.isArray(cart.discount_applications) ? cart.discount_applications.map(application) : cart.discount_applications, currency: cart.currency };
    return JSON.stringify({ cart: cartPricing, variants: Array.from(ids).sort().map((id) => {
      const v = index.variants.get(id);
      return v ? [id, v.price, v.compareAt, v.eligibility, v.available, v.backorder === true] : [id, null];
    }) });
  }

  async function loadCatalog(fetchPage) {
    let expectedPages;
    let expectedTotal;
    const products = [];
    for (let page = 1; expectedPages == null || page <= expectedPages; page += 1) {
      const data = await fetchPage(page);
      if (!data || data.page !== page || !Number.isSafeInteger(data.pages) || data.pages < 1 || data.pages > 500 || !Number.isSafeInteger(data.total) || data.total < 0 || !Array.isArray(data.products)) throw fail('incompleteCatalog');
      if (expectedPages == null) { expectedPages = data.pages; expectedTotal = data.total; }
      if (data.pages !== expectedPages || data.total !== expectedTotal || (page < expectedPages && !data.products.length)) throw fail('incompleteCatalog');
      products.push(...data.products);
      if (products.length > expectedTotal) throw fail('incompleteCatalog');
    }
    if (products.length !== expectedTotal) throw fail('incompleteCatalog');
    return indexCatalog(products);
  }

  function filterProducts(index, filters) {
    const query = String(filters.search || '').trim().toLocaleLowerCase();
    return index.products.filter((product) => {
      const haystack = [product.title, product.type, product.handle, ...product.tags, ...product.variants.map((v) => v.title + ' ' + (v.title || v.size))].join(' ').toLocaleLowerCase();
      return (!query || haystack.includes(query)) && (!filters.type || product.type === filters.type)
        && product.variants.some((variant) => (!filters.available || variant.available) && (!filters.eligibility || filters.eligibility === 'all' || (filters.eligibility === 'excluded' ? variant.eligibility !== 'regular' : variant.eligibility === filters.eligibility)));
    });
  }

  const api = { MAX_QUANTITY, classification, indexCatalog, aggregateSelections, validateCart, cartQuantities, lineDiscounted, discountContext, validateSelection, preview, tierFor, addPayload, sameQuantities, verifyAddition, snapshot, loadCatalog, filterProducts, normalizeSize, tierProgress, projectedTotal, canStageVariant, matchingSizeVariants, prioritizeProducts, sameSizeAlternatives, selectionIssue };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!root || !root.customElements || !root.HTMLElement || root.customElements.get('fs-bundle-builder')) return;

  const defaults = {
    loading: 'Loading the full catalog…', loadError: 'The complete catalog or cart could not be loaded. Retry before building.', ready: '{count} products loaded', results: '{count} of {total} products', noResults: 'No products match these filters.', allTypes: 'All categories', chooseSize: 'Choose a size', regular: 'Bundle eligible', sale: 'Sale item · excluded', unverified: 'Eligibility unverified', soldOut: 'Sold out', select: 'Add to bundle', selected: 'Added to bundle', variant: 'Size', quantity: 'Quantity', remove: 'Remove', showMore: 'Show more', empty: 'Your bundle is empty', tierNone: 'No tier yet', tier: '{percent}% projected', checking: 'Rechecking prices, availability and cart…', changed: 'Prices, availability or cart changed. Review the updated preview, then add again.', adding: 'Adding at current prices…', added: 'Items added at current prices. Review your cart before checkout.', addFailed: 'Shopify could not confirm this addition. Review your cart before trying again.', retry: 'Retry loading', locked: 'The cart outcome is uncertain. Open your cart to review; this builder will not resend the request.', invalidQuantity: 'Choose a whole quantity from 1 to 99.', stock: 'The requested quantity is no longer available.', unavailable: 'This variant is no longer available. Choose another.', cartDiscount: 'Existing discounts are excluded from this estimate. Checkout combinations are not configured yet.', error: 'Something changed. Retry loading the catalog and cart.', backorder: 'Available to order — backorder', quantityRule: 'This size has quantity limits. Review the quantity and try again.', selectedCount: '{count} selected', noImage: 'Image unavailable', add: 'Add selected items at current prices'
  };
  Object.assign(defaults, {
    anySize: 'Any size', sizeMatch: '{size} available', choosePreferredSize: 'Choose {size}', chooseSizeOption: 'Choose a {size} option', findAlternatives: 'Find alternatives in {size}', alternativesHeading: 'Same-size {type} alternatives · {size}', alternativesScope: 'Suggestions may be outside your current filters.', noAlternatives: 'No available regular-price {type} alternatives in {size}.', addAlternative: 'Add this instead', replaceAlternative: 'Replace with {title}', alternativeAdded: '{title} added to your bundle.', alternativeReplaced: 'Replaced with {title}.', tierNext: 'Add {count} more eligible item(s) for {percent}% projected savings', tierMaximum: 'Maximum tier · 10% projected savings', projectionPending: 'Preview unavailable', projectedTotalLabel: 'Projected merchandise total · preview only'
  });
  const escape = (value) => String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const assetUrl = (value) => {
    if (!value) return '';
    try { const url = new URL(value, root.location.origin); return url.protocol === 'https:' || url.protocol === 'http:' && url.origin === root.location.origin ? url.href : ''; } catch (_) { return ''; }
  };

  class BundleBuilder extends root.HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.selected = [];
      this.pickers = new Map();
      this.alternativeRequests = new Set();
      this.selectedDetails = new Map();
      this.limit = 12;
      this.busy = false;
      this.ready = false;
      this.locked = false;
      try { this.config = JSON.parse(this.querySelector('[data-fs-bundle-config]').textContent); }
      catch (_) { this.config = {}; }
      this.strings = { ...defaults, ...this.config.strings };
      this.storageKey = 'fs-bundle-pending-add';
      try { this.locked = Boolean(root.sessionStorage.getItem(this.storageKey)); } catch (_) {}
      this.addEventListener('click', (event) => this.click(event));
      this.addEventListener('change', (event) => this.change(event));
      this.addEventListener('input', (event) => {
        if (event.target.matches('[data-fs-search]')) { this.limit = 12; this.renderProducts(); }
        if (event.target.matches('[data-fs-selected-quantity]')) this.change(event);
      });
      this.addEventListener('submit', (event) => { event.preventDefault(); this.add(); });
      this.initialized = this.load();
    }

    el(name) { return this.querySelector('[data-fs-' + name + ']'); }
    t(key, values) { return String(this.strings[key] || this.strings.error).replace(/\{(\w+)\}/g, (match, name) => values && values[name] != null ? String(values[name]) : match); }
    text(name, value) { const node = this.el(name); if (node) node.textContent = value; }
    status(key) { const discountNote = this.cart && discountContext(this.cart, this.index).suppressProjection && key !== 'cartDiscount' ? ' ' + this.t('cartDiscount') : ''; this.text('summary-status', this.t(key) + discountNote); }
    money(value) {
      try { return new Intl.NumberFormat(this.config.locale || 'en', { style: 'currency', currency: this.cart && this.cart.currency || this.config.currency || 'PKR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value / 100); }
      catch (_) { return (value / 100).toFixed(2) + ' ' + (this.config.currency || 'PKR'); }
    }
    endpoint(value, fallback) {
      const url = new URL(value || fallback, root.location.origin);
      if (url.origin !== root.location.origin) throw fail('invalidEndpoint');
      return url;
    }
    async request(url, options) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await root.fetch(String(url), { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
        if (!response.ok) throw Object.assign(fail('request'), { status: response.status });
        return await response.json();
      } finally { clearTimeout(timer); }
    }
    catalog() {
      return loadCatalog((page) => {
        const url = this.endpoint(this.config.catalogUrl, '/collections/all?view=bundle-data');
        url.searchParams.set('page', String(page));
        url.searchParams.set('_fs', String(Date.now()));
        return this.request(url);
      });
    }
    readCart() {
      const url = this.endpoint(this.config.cartUrl, '/cart.js');
      url.searchParams.set('_fs', String(Date.now()));
      return this.request(url).then(validateCart).then((cart) => {
        const keys = ['currency', 'total_price', 'items_subtotal_price', 'original_total_price', 'total_discount', 'cart_level_discount_applications'];
        const itemKeys = ['variant_id', 'quantity', 'original_price', 'final_price', 'original_line_price', 'final_line_price', 'total_discount', 'line_level_discount_allocations', 'discounts'];
        const pricing = Object.fromEntries(keys.filter((key) => key in cart).map((key) => [key, cart[key]]));
        pricing.items = cart.items.map((item) => Object.fromEntries(itemKeys.filter((key) => key in item).map((key) => [key, item[key]])));
        let node = this.el('cart-pricing');
        if (!node) { node = document.createElement('script'); node.type = 'application/json'; node.dataset.fsCartPricing = ''; this.append(node); }
        node.textContent = JSON.stringify(pricing);
        return cart;
      });
    }
    setBusy(value) {
      this.busy = value;
      this.setAttribute('aria-busy', String(value));
      this.querySelectorAll('[data-fs-add], [data-fs-pick], [data-fs-remove], [data-fs-selected-quantity], [data-fs-choose-size], [data-fs-find-alternatives], [data-fs-use-alternative]').forEach((node) => { node.disabled = value || this.locked || !this.ready; });
      this.querySelectorAll('[data-fs-search], [data-fs-category], [data-fs-eligibility], [data-fs-available], [data-fs-preferred-size]').forEach((node) => { node.disabled = !this.ready; });
      this.querySelectorAll('[data-fs-pick]').forEach((node) => { const variant = this.index && this.index.variants.get(node.closest('[data-fs-product]').querySelector('[data-fs-variant]').value); node.disabled = value || this.locked || !this.ready || !variant || !variant.available; });
      if (this.el('products')) this.el('products').setAttribute('aria-busy', String(value));
      this.updateButton();
    }
    updateButton() {
      const button = this.el('add');
      if (button) { button.disabled = this.busy || this.locked || !this.ready || !this.selected.length || Boolean(this.selectionError); button.textContent = this.t(this.busy ? 'checking' : 'add'); }
    }
    async load() {
      if (this.busy) return;
      this.ready = false;
      this.setBusy(true);
      this.text('catalog-status', this.t('loading'));
      if (this.el('retry')) this.el('retry').hidden = true;
      try {
        const [index, cart] = await Promise.all([this.catalog(), this.readCart()]);
        this.index = index;
        this.cart = cart;
        this.ready = true;
        this.renderTypes();
        this.renderSizes();
        this.renderProducts();
        this.text('summary-status', '');
        this.renderSummary();
        if (this.locked) this.status('locked');
      } catch (_) {
        this.ready = false;
        this.text('catalog-status', this.t('loadError'));
        this.status('loadError');
        this.renderProjection(null);
        if (this.el('products')) this.el('products').replaceChildren();
        if (this.el('retry')) this.el('retry').hidden = false;
      } finally {
        this.setBusy(false);
        if (this.el('retry')) this.el('retry').disabled = false;
      }
    }
    renderTypes() {
      const select = this.el('category');
      if (!select) return;
      const value = select.value;
      const types = [...new Set(this.index.products.map((product) => product.type).filter(Boolean))].sort();
      select.innerHTML = '<option value="">' + escape(this.t('allTypes')) + '</option>' + types.map((type) => '<option value="' + escape(type) + '">' + escape(type) + '</option>').join('');
      if (types.includes(value)) Array.from(select.options).find((option) => option.value === value).selected = true;
    }
    renderSizes() {
      const select = this.el('preferred-size');
      if (!select) return;
      const value = select.value || '';
      const sizes = [...new Set(this.index.products.filter((product) => product.hasSize !== false).flatMap((product) => product.variants.map((variant) => normalizeSize(variant.size))).filter(Boolean))];
      if (value && !sizes.includes(value)) sizes.push(value);
      const canonical = ['XS', 'S', 'M', 'L', 'XL'];
      sizes.sort((a, b) => {
        const rankA = canonical.includes(a) ? canonical.indexOf(a) : canonical.length;
        const rankB = canonical.includes(b) ? canonical.indexOf(b) : canonical.length;
        return rankA - rankB || a.localeCompare(b);
      });
      select.innerHTML = '<option value="">' + escape(this.t('anySize')) + '</option>' + sizes.map((size) => '<option value="' + escape(size) + '"' + (size === value ? ' selected' : '') + '>' + escape(size) + '</option>').join('');
    }
    filters() {
      return { search: this.el('search') && this.el('search').value, type: this.el('category') && this.el('category').value, eligibility: this.el('eligibility') && this.el('eligibility').value, available: Boolean(this.el('available') && this.el('available').checked) };
    }
    preferredSize() { return this.el('preferred-size') ? this.el('preferred-size').value : ''; }
    focusNode(node, scroll = false) {
      if (!node) return;
      if (scroll && typeof node.scrollIntoView === 'function') node.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      if (typeof node.focus === 'function') node.focus();
    }
    sizeHelp(product) {
      const size = this.preferredSize();
      if (!size || product.hasSize === false) return '';
      const matches = matchingSizeVariants(this.index, this.cart, this.selected, product, size);
      const label = normalizeSize(size);
      const disabled = this.busy || this.locked ? ' disabled' : '';
      if (matches.length) return '<div class="fs-bundle__size-help"><span class="fs-bundle__size-match">' + escape(this.t('sizeMatch', { size: label })) + '</span><button type="button" class="fs-bundle__size-action" data-fs-choose-size="' + (matches.length === 1 ? matches[0].id : '') + '"' + disabled + '>' + escape(this.t(matches.length === 1 ? 'choosePreferredSize' : 'chooseSizeOption', { size: label })) + '</button></div>';
      return '<div class="fs-bundle__size-help"><button type="button" class="fs-bundle__size-action" data-fs-find-alternatives aria-expanded="' + this.alternativeRequests.has(product.id) + '" aria-controls="fs-alternatives-' + product.id + '"' + disabled + '>' + escape(this.t('findAlternatives', { size: label })) + '</button></div>' + (this.alternativeRequests.has(product.id) ? this.alternativesMarkup(product, size) : '');
    }
    alternativesMarkup(product, size, item) {
      const replacements = sameSizeAlternatives(this.index, this.cart, this.selected, product, size, item ? item.quantity : 1, item ? item.id : '');
      const label = normalizeSize(size);
      const id = 'fs-alternatives-' + (item ? 'selected-' + item.id : product.id);
      const heading = this.t('alternativesHeading', { type: product.type, size: label });
      const contents = replacements.map((variant) => {
        const title = variant.product.title + ' · ' + (variant.title || variant.size);
        const image = assetUrl(variant.product.image);
        return '<article class="fs-bundle__alternative">' + (image ? '<img class="fs-bundle__alternative-image" src="' + escape(image) + '" alt="" width="96" height="120" loading="lazy">' : '') + '<div class="fs-bundle__alternative-content"><strong>' + escape(title) + '</strong><span class="fs-bundle__alternative-price">' + escape(this.money(variant.price)) + '</span><small>' + escape(this.t('regular') + (variant.backorder ? ' · ' + this.t('backorder') : '')) + '</small><button type="button" class="fs-bundle__alternative-action" data-fs-use-alternative="' + variant.id + '" data-fs-alternative-source="' + product.id + '" data-fs-alternative-size="' + escape(size) + '"' + (item ? ' data-fs-replace-id="' + escape(item.id) + '"' : '') + (this.busy || this.locked ? ' disabled' : '') + ' aria-label="' + escape(this.t(item ? 'replaceAlternative' : 'addAlternative', { title }) + (item ? '' : ': ' + title)) + '">' + escape(this.t(item ? 'replaceAlternative' : 'addAlternative', { title })) + '</button></div></article>';
      }).join('');
      return '<section class="fs-bundle__alternatives" id="' + id + '" tabindex="-1" aria-label="' + escape(heading) + '"><p class="fs-bundle__alternatives-heading">' + escape(heading) + '</p>' + (contents ? '<p class="fs-bundle__alternatives-scope">' + escape(this.t('alternativesScope')) + '</p>' + contents : '<p class="fs-bundle__alternatives-empty" role="status">' + escape(this.t('noAlternatives', { type: product.type, size: label })) + '</p>') + '</section>';
    }
    variantStatus(variant) { return !variant ? '' : !variant.available ? this.t('soldOut') : this.t(variant.eligibility) + (variant.backorder ? ' · ' + this.t('backorder') : ''); }
    variantLabel(variant) { return String(variant.title || variant.size || '').split(' / ').map((part) => normalizeSize(part.trim())).join(' / '); }
    renderProducts() {
      if (!this.ready || !this.el('products')) return;
      const products = prioritizeProducts(filterProducts(this.index, this.filters()), this.index, this.cart, this.selected, this.preferredSize());
      const shown = products.slice(0, this.limit);
      this.el('products').innerHTML = shown.map((product) => {
        const picker = this.pickers.get(product.id) || { id: '', quantity: 1 };
        const variant = this.index.variants.get(picker.id);
        const image = assetUrl(product.image);
        const url = assetUrl(product.url);
        const title = '<button type="button" data-fs-bundle-product="' + product.id + '">' + escape(product.title) + '</button>';
        return '<article class="fs-bundle__product" data-fs-product="' + product.id + '">' + '<button type="button" class="fs-bundle__product-image" data-fs-bundle-product="' + product.id + '" aria-label="' + escape(product.title) + '">' + (image ? '<img src="' + escape(image) + '" alt="' + escape(product.title) + '" loading="lazy" width="480" height="600">' : '<span class="fs-bundle__image-fallback">' + escape(this.t('noImage')) + '</span>') + '</button>' + '<div class="fs-bundle__product-info"><h3 class="fs-bundle__product-title">' + title + '</h3><p class="fs-bundle__product-price">' + (variant ? escape(this.money(variant.price)) : escape(this.money(Math.min(...product.variants.map((v) => v.price))))) + '</p><p class="fs-bundle__product-label" data-fs-variant-label>' + escape(this.variantStatus(variant)) + '</p>' + this.sizeHelp(product) + '<div class="fs-bundle__product-controls"><label class="fs-bundle__variant"><span class="fs-bundle__visually-hidden">' + escape(this.t('variant')) + '</span><select data-fs-variant aria-label="' + escape(product.title + ' · ' + this.t('variant')) + '"><option value="">' + escape(this.t('chooseSize')) + '</option>' + product.variants.map((v) => '<option value="' + v.id + '"' + (v.id === picker.id ? ' selected' : '') + (!v.available ? ' disabled' : '') + '>' + escape(this.variantLabel(v)) + '</option>').join('') + '</select></label><label class="fs-bundle__quantity"><span class="fs-bundle__visually-hidden">' + escape(this.t('quantity')) + '</span><input type="number" inputmode="numeric" min="1" max="99" step="1" value="' + escape(picker.quantity) + '" data-fs-pick-quantity aria-label="' + escape(product.title + ' · ' + this.t('quantity')) + '"></label></div><button type="button" class="fs-bundle__pick" data-fs-pick' + (!variant || !variant.available || this.busy || this.locked ? ' disabled' : '') + '>' + escape(this.t('select')) + '</button><p class="fs-bundle__card-status" data-fs-card-status role="status" aria-live="polite"></p></div></article>';
      }).join('') || '<p class="fs-bundle__empty">' + escape(this.t('noResults')) + '</p>';
      this.querySelectorAll('.fs-bundle__product-image img').forEach((image) => {
        const fallback = () => { const node = this.ownerDocument.createElement('span'); node.className = 'fs-bundle__image-fallback'; node.textContent = this.t('noImage'); image.replaceWith(node); };
        image.addEventListener('error', fallback, { once: true });
        if (image.complete && image.naturalWidth === 0) fallback();
      });
      this.text('catalog-status', this.t('results', { count: products.length, total: this.index.products.length }));
      const pagination = this.el('pagination');
      if (pagination) pagination.innerHTML = products.length > shown.length ? '<button type="button" class="fs-bundle__more" data-fs-more>' + escape(this.t('showMore')) + '</button>' : '';
    }
    renderProjection(model) {
      const progress = tierProgress(model);
      const message = progress ? this.t(progress.maximum ? 'tierMaximum' : 'tierNext', { count: progress.remaining, percent: progress.nextTier }) : this.t('projectionPending');
      this.querySelectorAll('[data-fs-tier-progress]').forEach((node) => { node.textContent = message; });
      this.querySelectorAll('[data-fs-tier-meter]').forEach((node) => {
        node.hidden = !progress;
        node.setAttribute('role', 'progressbar');
        node.setAttribute('aria-label', message);
        node.setAttribute('aria-valuemin', '0');
        node.setAttribute('aria-valuemax', '4');
        node.setAttribute('aria-valuenow', String(progress ? progress.value : 0));
        node.setAttribute('aria-valuetext', message);
        node.style.setProperty('--fs-tier-progress', (progress ? progress.value * 25 : 0) + '%');
      });
      const total = projectedTotal(model);
      this.text('projected-total', total === null ? this.t('projectionPending') : this.money(total));
      const node = this.el('projected-total');
      if (node) node.setAttribute('aria-label', this.t('projectedTotalLabel') + ': ' + node.textContent);
    }
    renderSummary() {
      if (!this.ready) return;
      let model;
      this.selectionError = null;
      try { model = preview(this.index, this.cart, this.selected); }
      catch (error) { this.selectionError = error; this.status(this.strings[error.code] ? error.code : 'changed'); }
      this.renderProjection(model);
      const list = this.el('selected');
      if (list) list.innerHTML = this.selected.map((item) => {
        const v = this.index.variants.get(item.id);
        if (v) this.selectedDetails.set(item.id, v);
        const remembered = v || this.selectedDetails.get(item.id);
        const issue = selectionIssue(this.index, this.cart, item);
        const replacementPossible = issue && ['unavailable', 'stock', 'unknownVariant'].includes(issue.code) && remembered && remembered.product.hasSize !== false;
        const replacement = replacementPossible ? this.alternativesMarkup(remembered.product, remembered.size, item) : '';
        const issueMarkup = issue ? '<small class="fs-bundle__selection-error" role="status">' + escape(this.t(this.strings[issue.code] ? issue.code : 'unavailable')) + '</small>' : '';
        const title = remembered ? remembered.product.title : this.t('unavailable');
        const projectionLine = model && model.lines.find((line) => line.id === item.id);
        const eligibilityLabel = v && v.eligibility === 'regular' && projectionLine && !projectionLine.eligible ? this.t('cartDiscount') : this.t(v ? v.eligibility : 'unverified');
        return '<li class="fs-bundle__selected-item" data-fs-selected-id="' + escape(item.id) + '"><div class="fs-bundle__selected-info"><strong>' + escape(title) + '</strong><span>' + escape(v ? (v.title || v.size) + ' · ' + this.money(v.price) : this.t('unavailable')) + '</span><small>' + escape(eligibilityLabel) + '</small>' + issueMarkup + '</div><label class="fs-bundle__quantity">' + escape(this.t('quantity')) + '<input type="number" inputmode="numeric" min="1" max="99" step="1" data-fs-selected-quantity value="' + escape(item.quantity) + '" aria-label="' + escape(title + ' · ' + this.t('quantity')) + '"' + (this.busy || this.locked ? ' disabled' : '') + '></label><button type="button" class="fs-bundle__remove" data-fs-remove aria-label="' + escape(this.t('remove') + ' ' + title + (v ? ' ' + (v.title || v.size) : '')) + '"' + (this.busy || this.locked ? ' disabled' : '') + '>' + escape(this.t('remove')) + '</button>' + replacement + '</li>';
      }).join('');
      if (this.el('selected-empty')) this.el('selected-empty').hidden = this.selected.length > 0;
      this.text('cart-subtotal', this.money(cents(this.cart.items_subtotal_price) ? this.cart.items_subtotal_price : this.cart.total_price));
      this.text('selected-subtotal', model ? this.money(model.selectedSubtotal) : '—');
      this.text('eligible-units', model ? String(model.eligibleUnits) : '—');
      this.text('eligible-subtotal', model ? this.money(model.eligibleSubtotal) : '—');
      this.text('tier', model && model.tier ? this.t('tier', { percent: model.tier }) : this.t('tierNone'));
      this.text('savings', model ? this.money(model.savings) : '—');
      if (this.el('mobile-review')) this.el('mobile-review').hidden = !this.selected.length;
      this.text('mobile-count', this.t('selectedCount', { count: this.selected.reduce((sum, item) => sum + Number(item.quantity || 0), 0) }));
      this.text('mobile-savings', model ? this.money(model.savings) : '—');
      this.toggleAttribute('data-fs-has-selection', this.selected.length > 0);
      if (model && model.suppressProjection) this.status('cartDiscount');
      this.updateButton();
    }
    change(event) {
      const target = event.target;
      if (target.matches('[data-fs-preferred-size]')) { this.alternativeRequests.clear(); this.limit = 12; this.renderProducts(); return; }
      if (target.matches('[data-fs-category], [data-fs-eligibility], [data-fs-available]')) { this.limit = 12; this.renderProducts(); return; }
      if (this.busy || this.locked || !this.ready) return;
      if (target.matches('[data-fs-variant], [data-fs-pick-quantity]')) {
        const card = target.closest('[data-fs-product]');
        this.pickers.set(card.dataset.fsProduct, { id: card.querySelector('[data-fs-variant]').value, quantity: card.querySelector('[data-fs-pick-quantity]').value });
        if (target.matches('[data-fs-variant]')) {
          const v = this.index.variants.get(target.value);
          card.querySelector('[data-fs-variant-label]').textContent = this.variantStatus(v);
          card.querySelector('.fs-bundle__product-price').textContent = v ? this.money(v.price) : '';
          card.querySelector('[data-fs-pick]').disabled = !v || !v.available;
        }
      }
      if (target.matches('[data-fs-selected-quantity]')) {
        const item = this.selected.find((value) => value.id === target.closest('[data-fs-selected-id]').dataset.fsSelectedId);
        if (!item) return;
        try {
          const next = quantity(target.value);
          const candidate = this.selected.map((value) => value === item ? { ...value, quantity: next } : value);
          validateSelection(this.index, this.cart, candidate);
          item.quantity = next;
          this.selectionError = null;
          target.removeAttribute('aria-invalid');
          this.text('summary-status', '');
          const focusedId = item.id;
          this.renderSummary();
          this.renderProducts();
          const replacement = this.querySelector('[data-fs-selected-id="' + focusedId + '"] [data-fs-selected-quantity]');
          if (replacement) replacement.focus();
        } catch (error) { this.selectionError = error; target.setAttribute('aria-invalid', 'true'); this.status(this.strings[error.code] ? error.code : 'changed'); this.renderProjection(null); this.updateButton(); }
      }
    }
    click(event) {
      const target = event.target.closest('button');
      if (!target || !this.contains(target)) return;
      if (target.matches('[data-fs-retry]')) { event.preventDefault(); this.load(); return; }
      if (target.matches('[data-fs-more]')) { this.limit += 12; this.renderProducts(); return; }
      if (target.matches('[data-fs-add]')) { event.preventDefault(); this.add(); return; }
      if (this.busy || this.locked || !this.ready) return;
      if (target.matches('[data-fs-choose-size]')) {
        const card = target.closest('[data-fs-product]');
        const product = this.index.products.find((item) => item.id === card.dataset.fsProduct);
        const matches = matchingSizeVariants(this.index, this.cart, this.selected, product, this.preferredSize());
        const select = card.querySelector('[data-fs-variant]');
        if (matches.length === 1 && target.dataset.fsChooseSize === matches[0].id) {
          Array.from(select.options).find((option) => option.value === matches[0].id).selected = true;
          this.change({ target: select });
        }
        this.focusNode(select);
        return;
      }
      if (target.matches('[data-fs-find-alternatives]')) {
        const id = target.closest('[data-fs-product]').dataset.fsProduct;
        this.alternativeRequests.add(id);
        this.renderProducts();
        this.focusNode(this.querySelector('#fs-alternatives-' + id), true);
        return;
      }
      if (target.matches('[data-fs-use-alternative]')) {
        const replaceId = target.dataset.fsReplaceId || '';
        const item = replaceId ? this.selected.find((value) => value.id === replaceId) : null;
        const oldVariant = replaceId && (this.index.variants.get(replaceId) || this.selectedDetails.get(replaceId));
        const product = this.index.products.find((value) => value.id === target.dataset.fsAlternativeSource) || oldVariant && oldVariant.product;
        if (!product || replaceId && (!item || !['unavailable', 'stock', 'unknownVariant'].includes((selectionIssue(this.index, this.cart, item) || {}).code))) return;
        const size = replaceId ? oldVariant && oldVariant.size : this.preferredSize();
        const variant = sameSizeAlternatives(this.index, this.cart, this.selected, product, size, item ? item.quantity : 1, replaceId).find((value) => value.id === target.dataset.fsUseAlternative);
        if (!variant) { this.renderProducts(); this.renderSummary(); return; }
        const candidate = aggregateSelections([...this.selected.filter((value) => value.id !== replaceId), { id: variant.id, quantity: item ? item.quantity : 1 }]);
        this.selected = candidate;
        this.renderProducts();
        this.renderSummary();
        const title = variant.product.title + ' · ' + (variant.title || variant.size);
        if (!this.selectionError) this.text('summary-status', this.t(item ? 'alternativeReplaced' : 'alternativeAdded', { title }));
        this.focusNode(this.querySelector('[data-fs-selected-id="' + variant.id + '"] [data-fs-selected-quantity]'), true);
        return;
      }
      if (target.matches('[data-fs-remove]')) {
        const id = target.closest('[data-fs-selected-id]').dataset.fsSelectedId;
        const position = this.selected.findIndex((item) => item.id === id);
        this.selected = this.selected.filter((item) => item.id !== id);
        this.text('summary-status', '');
        this.renderSummary();
        this.renderProducts();
        const buttons = this.querySelectorAll('[data-fs-remove]');
        const focus = buttons[Math.min(position, buttons.length - 1)] || this.el('search');
        if (focus) focus.focus();
      }
      if (target.matches('[data-fs-pick]')) {
        const card = target.closest('[data-fs-product]');
        try {
          const candidate = aggregateSelections([...this.selected, { id: card.querySelector('[data-fs-variant]').value, quantity: card.querySelector('[data-fs-pick-quantity]').value }]);
          validateSelection(this.index, this.cart, candidate);
          this.selected = candidate;
          this.renderSummary();
          this.renderProducts();
          this.status('selected');
          const refreshed = this.querySelector('[data-fs-product="' + card.dataset.fsProduct + '"]');
          if (refreshed) { refreshed.querySelector('[data-fs-card-status]').textContent = this.t('selected'); this.focusNode(refreshed.querySelector('[data-fs-pick]')); }
        } catch (error) { const key = this.strings[error.code] ? error.code : 'changed'; this.status(key); card.querySelector('[data-fs-card-status]').textContent = this.t(key); }
      }
    }
    pending(value) {
      this.locked = value;
      try { if (value) root.sessionStorage.setItem(this.storageKey, '1'); else root.sessionStorage.removeItem(this.storageKey); } catch (_) {}
    }
    async add() {
      if (this.busy || this.locked || !this.ready || this.selectionError || !this.selected.length) return;
      this.setBusy(true);
      this.status('checking');
      const selected = this.selected.map((item) => ({ ...item }));
      let attempted = false;
      let before;
      try {
        const previous = snapshot(this.index, this.cart, selected);
        const [index, cart] = await Promise.all([this.catalog(), this.readCart()]);
        this.index = index;
        this.cart = cart;
        before = cart;
        this.renderSizes();
        this.renderProducts();
        this.renderSummary();
        if (this.selectionError) return;
        const payload = addPayload(index, cart, selected);
        const cartPage = this.endpoint(this.config.cartPageUrl, '/cart');
        this.renderProducts();
        this.renderSummary();
        if (previous !== snapshot(index, cart, selected)) { this.status('changed'); return; }
        this.status('adding');
        const endpoint = this.endpoint(this.config.cartAddUrl, '/cart/add.js');
        this.pending(true);
        attempted = true;
        let requestError;
        try { await this.request(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload) }); }
        catch (error) { requestError = error; }
        const after = await this.readCart();
        this.cart = after;
        if (verifyAddition(before, after, selected)) {
          this.pending(false);
          this.selected = [];
          this.renderSummary();
          this.status('added');
          this.dispatchEvent(new root.CustomEvent('fs:bundle-added', { bubbles: true, detail: { cart: after } }));
          root.location.assign(cartPage.href);
        } else if (requestError && requestError.status >= 400 && requestError.status < 500 && sameQuantities(before, after)) {
          this.pending(false);
          this.renderSummary();
          this.status('addFailed');
        } else { this.pending(true); this.renderSummary(); this.status('locked'); }
      } catch (error) {
        if (attempted) { this.pending(true); this.status('locked'); }
        else { this.ready = false; this.renderProjection(null); this.status(this.strings[error.code] ? error.code : 'loadError'); if (this.el('retry')) this.el('retry').hidden = false; }
      } finally {
        this.setBusy(false);
        if (this.el('retry')) this.el('retry').disabled = false;
        this.renderProducts();
      }
    }
  }
  root.customElements.define('fs-bundle-builder', BundleBuilder);
})(typeof window !== 'undefined' ? window : null);
